import React, { FC, useEffect, useState, useRef } from "react";
import { Modal, Select, TreeSelect } from "antd";
import { useQuery, useMutation } from "@apollo/client";
import { useVpdClientReportEngine, UPDATE_CLIENT_REPORT_OPTIONS } from "~/hooks/";
import { filteredDesignsQuery } from "~/components/docx-proposal/graphql-queries";
import { Editor } from "@tinymce/tinymce-react";
import { Editor as TinyMCEEditor } from "tinymce";
import { LoadingSkeleton } from "~/components/page-loading/skeleton/loading-skeleton";
import { GroupedEntityType, OutputDesignType } from "~/gql/graphql";
import { useRestApi } from "~/hooks";
import { EvaChart } from "../earned-value-analysis";
import styles from "./report-preview.css";
import { renderToString } from "react-dom/server";
import { useGroupItems } from "~/components/groups/hooks/use-group-items";

import "../../../components/docx-proposal/font-wrapper.css";
import { useHasInvoicesQuery } from "~/graphql/query";
// import { SelectDesignModal, getBeforeandAfterParts } from "~/containers/proposal-preview/";

const { ["container"]: containerClassName, ["workspace"]: workspaceClassName } = styles as any;

const editorCss = `
/* Create a dashed line background */
@import url('https://fonts.googleapis.com/css2?family=Carlito:ital,wght@0,400;0,700;1,400;1,700&display=swap');
body {
  padding: 30px 96px 30px 96px !important;
}
ol {
  margin: 0 !important;
  padding:0;
}
ol > li {
  position: relative;
  padding-left: 35px;
}
page-break: {
  background-color: red !important;
}
  .dashed-line {
      border-bottom: 2px dashed #000; /* Adjust the color and style as needed */
      margin-bottom: 500px; /* Adjust the margin as needed */
    }
`;

export const getBeforeandAfterParts = (content: string) => {
  // Create a temporary div element
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = content;

  console.log({ content });

  // Find the target div by its id
  const targetDiv = tempDiv.querySelector("#vpd-main-content");

  if (!targetDiv) {
    return {
      before: "",
      after: "",
    };
  }

  // Extract everything before the target div
  let before = "";
  let node = targetDiv.previousSibling;
  while (node) {
    if (node instanceof Element) {
      before = node.outerHTML + before;
    }
    node = node.previousSibling;
  }

  // Extract everything after the target div
  let after = "";
  node = targetDiv.nextSibling;
  while (node) {
    if (node instanceof Element) {
      after += node.outerHTML;
    }
    node = node.nextSibling;
  }

  return {
    before,
    after,
  };
};

type SelectDesignModalProps = {
  open: boolean;
  close: () => void;
  onSelect: (designId: number) => void;
};
export const SelectDesignModal: React.FC<SelectDesignModalProps> = ({ open, close, onSelect }) => {
  const { data: groupItems, groupedForCascade } = useGroupItems(GroupedEntityType.ClientReport);
  const [selectedGroupItem, setSelectedGroupItem] = React.useState<number | null>(null);
  const [selectedDesign, setSelectedDesign] = React.useState<number | null>(null);

  return (
    <Modal
      confirmLoading
      title="Select Design"
      onCancel={() => close()}
      open={open}
      footer={null}
      onOk={() => (selectedDesign ? onSelect(selectedDesign) : close())}
    >
      <div style={{ width: "100%" }}>
        <TreeSelect
          onSelect={(val) => setSelectedGroupItem(val)}
          style={{ width: "100%" }}
          treeData={groupedForCascade}
        />
        {selectedGroupItem && (groupItems?.groupItems.length ?? 0) > 0 && (
          <Select
            options={groupItems?.groupItems
              .find((x) => x.id == selectedGroupItem)
              ?.outputDesigns?.filter((x) => x.active)
              .map((x) => ({
                label: x.title,
                value: x.id,
              }))}
            style={{ width: "100%", marginTop: 8 }}
            onSelect={(val) => {
              onSelect(val);
              close();
            }}
            placeholder="Select a design"
          />
        )}
      </div>
    </Modal>
  );
};

type ReportPreviewProps = {
  matterId: number;
};

export const ReportPreview: FC<ReportPreviewProps> = ({ matterId }) => {
  const restApi = useRestApi();
  const downloadRef = useRef<any>(null);
  const [editorLoading, setEditorLoading] = useState(true);
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const [chartSvgUpdated, setChartSvgUpdated] = useState(false);
  const { data: hasInvoices } = useHasInvoicesQuery();

  const [showSelectDesignModal, setShowSelectDesignModal] = useState(false);

  const { refetch, isLoading, updateClientReportHtml, initialCompiledContent } =
    useVpdClientReportEngine(matterId, "");
  const clientReport = initialCompiledContent?.clientReport;

  const { data: activeDesignTemplates } = useQuery(filteredDesignsQuery, {
    variables: {
      filter: {
        type: { equals: OutputDesignType.ClientReport },
        active: { equals: true },
      },
    },
  });

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    initialCompiledContent?.outputTemplateId ?? null
  );

  const [updateClientReportOptions] = useMutation(UPDATE_CLIENT_REPORT_OPTIONS);

  const handleEditorInit = (editor: TinyMCEEditor) => {
    editorRef.current = editor;

    setEditorLoading(false);
  };

  const chartSvg = clientReport
    ? renderToString(
        <EvaChart
          title="Cost Projection"
          data={
            clientReport.cumulativeAccruedFees?.map((caf) => ({
              x: caf.date,
              y: caf.amount,
            })) || []
          }
          completionLogs={
            clientReport.matterCompletionLogs?.map((cl) => ({
              x: cl.createdAt,
              y: cl.completionPercentage,
            })) ?? []
          }
          completionPercentage={
            clientReport.matterCompletionLogs && clientReport.matterCompletionLogs.length > 0
              ? clientReport.matterCompletionLogs[clientReport.matterCompletionLogs.length - 1]
                  .completionPercentage
              : 0
          }
          plannedFee={clientReport.plannedFee}
          currencySymbol={clientReport.currencySymbol}
          deadline={clientReport.deadline}
        />
      )
    : "";

  useEffect(() => {
    if (chartSvg && !chartSvgUpdated) {
      refetch({
        matterId,
        costProjectionSvgString: chartSvg,
      });
      setChartSvgUpdated(true);
    }
  }, [chartSvg]);

  return (
    <>
      <div className={containerClassName}>
        <div className={workspaceClassName}>
          {isLoading ||
            (!!initialCompiledContent && editorLoading && (
              <LoadingSkeleton count={11} loading={editorLoading} />
            ))}

          {initialCompiledContent?.clientReport &&
            (activeDesignTemplates?.filteredDesigns?.length ?? 0) > 0 && (
              <div style={{ height: "100%", maxWidth: "100%" }}>
                <Editor
                  scriptLoading={{ async: true, defer: true }}
                  initialValue={initialCompiledContent?.html || ""}
                  tinymceScriptSrc={`https://assets.vpd.law/vpd-ui/tinymce/js/tinymce/tinymce.min.js`}
                  onBlur={async (event, editor) => {
                    console.log({ event });
                    const htmlContent = editor.getContent({ format: "html" });
                    const parts = getBeforeandAfterParts(htmlContent);

                    updateClientReportHtml({
                      variables: {
                        update: {
                          designTemplateId: initialCompiledContent.outputTemplateId,
                          matterId,
                          htmlContent,
                          parts: [
                            {
                              html: parts.before,
                              before: true,
                            },
                            {
                              html: parts.after,
                              after: true,
                            },
                          ],
                        },
                      },
                      onCompleted: () => {
                        refetch().then((response) => {
                          const updatedHtml = response.data.clientReportByMatterId.html;
                          if (editorRef.current) {
                            editorRef.current.setContent(updatedHtml);
                          }
                        });
                      },
                    });
                  }}
                  onInit={(_, ed) => {
                    setEditorLoading(false);
                    handleEditorInit(ed);
                  }}
                  init={{
                    licenseKey: "gpl",
                    promotion: false,
                    font_family_formats:
                      "Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Calibri=Carlito; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Oswald=oswald; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats",
                    menu: {
                      custom: {
                        title: "Client Report Options",
                        items: `${
                          hasInvoices?.hasInvoices ? "includeBillingChart " : ""
                        }includeCostProjection`,
                      },
                      downloadPdf: {
                        title: "Export Client Report",
                        items: "downloadClientReportPdf",
                      },
                      changeDesign: {
                        title: `Change Design`,
                        items: "changeDesign",
                      },
                    },
                    verify_html: false,
                    elementpath: false,
                    branding: false,
                    lists_indent_on_tab: false,
                    extended_valid_elements:
                      "svg[*],defs[*],g[*],path[*],a[href|target=_blank],a[*]",
                    allow_unsafe_link_target: true,
                    indent_use_margin: true,
                    advlist_number_styles: "decimal",
                    highlight_on_focus: false,
                    editable_root: true,
                    editable_class: "editable",
                    content_css: "document",
                    content_style: editorCss,
                    pagebreak_separator: `<div style="page-break-before:always">&nbsp;</div> `,
                    newline_behavior: "invert",
                    setup: (ed) => {
                      console.log("Setting up");
                      let toggleCostProjectionDiagram =
                        initialCompiledContent?.clientReport.includeCostProjectionDiagram || false;
                      let toggleBillingChart =
                        initialCompiledContent?.clientReport.includeBillingChart || false;

                      ed.ui.registry.addMenuItem("downloadClientReportPdf", {
                        text: "Download as PDF",
                        onAction: async () => {
                          ed.setProgressState(true);
                          const pdfDownload = await restApi.downloadClientReportPdf(
                            ed.getContent({ format: "html" }),
                            matterId
                          );
                          await pdfDownload.download(downloadRef.current);
                          ed.setProgressState(false);
                        },
                      });

                      ed.ui.registry.addToggleMenuItem("changeDesign", {
                        text: `Change Design (${
                          initialCompiledContent?.outputTemplateName ?? "Default"
                        })`,
                        onAction: () => {
                          setShowSelectDesignModal(true);
                        },
                      });

                      ed.ui.registry.addToggleMenuItem("includeCostProjection", {
                        text: "Include Cost Projection",
                        onAction: () => {
                          ed.setProgressState(true);
                          updateClientReportOptions({
                            variables: {
                              updateOptions: {
                                matterId,
                                includeCostProjectionDiagram: !toggleCostProjectionDiagram,
                              },
                            },
                            onCompleted: () => {
                              toggleCostProjectionDiagram = !toggleCostProjectionDiagram;
                              ed.setProgressState(false, 750);
                              refetch();
                            },
                          });
                        },
                        onSetup(api) {
                          api.setActive(toggleCostProjectionDiagram);
                        },
                      });

                      ed.ui.registry.addToggleMenuItem("includeBillingChart", {
                        text: "Include Billing Chart",
                        onAction: () => {
                          ed.setProgressState(true);
                          updateClientReportOptions({
                            variables: {
                              updateOptions: {
                                matterId,
                                includeBillingChart: !toggleBillingChart,
                              },
                            },
                            onCompleted: () => {
                              toggleBillingChart = !toggleBillingChart;
                              ed.setProgressState(false, 750);
                              refetch();
                            },
                          });
                        },
                        onSetup(api) {
                          api.setActive(toggleBillingChart);
                        },
                      });

                      ed.on("keydown", function (event) {
                        if (event.key == "Tab") {
                          ed.execCommand("Indent");

                          event.preventDefault();
                          return false;
                        }
                      });

                      ed.on("backspace", function (e) {
                        console.log("backspace", e);
                      });
                    },
                    height: "100%",
                    width: "100%",
                    resize: false,
                    menubar: "view edit insert format help custom changeDesign downloadPdf",
                    plugins:
                      "pagebreak mentions anchor autolink charmap image link lists media searchreplace table wordcount checklist casechange",
                    toolbar:
                      "pagebreak undo redo | fontfamily fontsize | bold italic underline strikethrough | link image table | align lineheight | checklist numlist bullist indent outdent | charmap | removeformat",
                    mergetags: true,
                    ai_request: (
                      request: any,
                      respondWith: { string: (arg0: () => Promise<void>) => any }
                    ) =>
                      respondWith.string(() =>
                        Promise.reject("See docs to implement AI Assistant")
                      ),
                  }}
                />
                <a ref={(e) => (downloadRef.current = e || undefined)} />
              </div>
            )}
        </div>
      </div>
      {(activeDesignTemplates?.filteredDesigns?.length ?? 0) > 0 && (
        <SelectDesignModal
          onSelect={(designId) => {
            setSelectedTemplateId(designId);
            const newTemplate = activeDesignTemplates?.filteredDesigns?.find(
              (x) => x.id === designId
            );
            if (!editorRef.current || !newTemplate) return;

            editorRef.current.ui.registry.addToggleMenuItem("changeDesign", {
              text: `Change Design (${newTemplate?.title ?? "Default"})`,
              onAction: () => {
                setShowSelectDesignModal(true);
              },
            });
            const htmlContent = editorRef.current?.getContent({ format: "html" });
            const parts = getBeforeandAfterParts(htmlContent);
            updateClientReportHtml({
              variables: {
                update: {
                  designTemplateId: designId,
                  matterId,
                  htmlContent,
                  parts: [
                    {
                      html: parts.before,
                      before: true,
                    },
                    {
                      html: parts.after,
                      after: true,
                    },
                  ],
                  designChanged: true,
                },
              },
              onCompleted: () => {
                refetch().then((response) => {
                  const updatedHtml = response.data.clientReportByMatterId.html;
                  if (editorRef.current) {
                    editorRef.current.setContent(updatedHtml);
                  }
                });
              },
            });
          }}
          close={() => setShowSelectDesignModal(false)}
          open={showSelectDesignModal}
        />
      )}
    </>
  );
};
